import React, { useState, useEffect, useMemo } from 'react';
import { Shuffle, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CountryMultiSelect } from '@/components/country-multi-select';
import { countries } from '@/data/countries';
import { useTranslation } from 'react-i18next';
import { FlowStep, StepVariant } from '../types';

interface ManageVariantsDialogProps {
  step: FlowStep;
  onTransformToSplit: (variants: StepVariant[], splitType: 'weighted' | 'geo') => void;
  onUpdateVariants: (variants: StepVariant[], splitType?: 'weighted' | 'geo') => void;
  onRemoveSplit?: (variantToKeep: StepVariant) => void;
  children?: React.ReactNode;
  open?: boolean; // External control
  onOpenChange?: (open: boolean) => void; // External control
}

/**
 * Dialog to transform a standard step into a split step
 * or manage existing variants
 */
export function ManageVariantsDialog({
  step,
  onTransformToSplit,
  onUpdateVariants,
  onRemoveSplit,
  children,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}: ManageVariantsDialogProps) {
  const { t } = useTranslation('storefront');
  const [internalOpen, setInternalOpen] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedVariantToKeep, setSelectedVariantToKeep] = useState<string>('');

  // Use external control if provided, otherwise internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;

  const isStandardStep = !step.variants || step.variants.length === 0;
  const currentVariants = step.variants || [];

  // Split type
  const [splitType, setSplitType] = useState<'weighted' | 'geo'>(step.splitType || 'weighted');

  // ✅ FIX: Use local state for variants to avoid stale closure issues
  // This prevents race conditions when user quickly changes regions on multiple variants
  const [localVariants, setLocalVariants] = useState<StepVariant[]>(() =>
    currentVariants.map((v) => ({
      ...v,
      regions: v.regions || [],
    })),
  );

  // Sync local state with props when step.variants changes (from external source)
  useEffect(() => {
    const newVariants = currentVariants.map((v) => ({
      ...v,
      regions: v.regions || [],
    }));
    // Only update if the variants have actually changed from external source
    // Compare by stringifying to detect deep changes
    const currentJson = JSON.stringify(
      localVariants.map((v) => ({ id: v.id, regions: v.regions, percentage: v.percentage, label: v.label })),
    );
    const newJson = JSON.stringify(
      newVariants.map((v) => ({ id: v.id, regions: v.regions, percentage: v.percentage, label: v.label })),
    );
    if (currentJson !== newJson) {
      setLocalVariants(newVariants);
    }
  }, [step.variants]);

  // Use local variants for display and updates
  const normalizedCurrentVariants = localVariants;

  // For standard step transformation - start with 2 variants at 50/50
  const [newVariants, setNewVariants] = useState<
    Array<{ label: string; percentage: number; regions?: string[] }>
  >([
    { label: 'A', percentage: 50, regions: [] },
    { label: 'B', percentage: 50, regions: [] },
  ]);

  const handleAddNewVariantToCreation = () => {
    const nextLabel = String.fromCharCode(65 + newVariants.length); // A, B, C, D...
    setNewVariants([...newVariants, { label: nextLabel, percentage: 0, regions: [] }]);
  };

  const handleRemoveNewVariant = (index: number) => {
    if (newVariants.length <= 2) return; // Must have at least 2
    const filtered = newVariants.filter((_, i) => i !== index);
    setNewVariants(filtered);
  };

  const handleUpdateNewVariant = (
    index: number,
    field: 'label' | 'percentage' | 'regions',
    value: string | number | string[],
  ) => {
    setNewVariants(newVariants.map((v, i) => (i === index ? { ...v, [field]: value } : v)));
  };

  const handleAutoNormalize = () => {
    const total = newVariants.reduce((sum, v) => sum + v.percentage, 0);
    if (total === 0) {
      // Equal distribution if all are 0
      const equalPercentage = Math.floor(100 / newVariants.length);
      const remainder = 100 - equalPercentage * newVariants.length;
      setNewVariants(
        newVariants.map((v, i) => ({
          ...v,
          percentage: i === 0 ? equalPercentage + remainder : equalPercentage,
        })),
      );
    } else {
      // Proportional normalization based on current values
      const normalized = newVariants.map((v) => ({
        ...v,
        percentage: Math.round((v.percentage / total) * 100),
      }));

      // Fix rounding errors by adjusting the first variant
      const newTotal = normalized.reduce((sum, v) => sum + v.percentage, 0);
      if (newTotal !== 100) {
        normalized[0].percentage += 100 - newTotal;
      }

      setNewVariants(normalized);
    }
  };

  const handleTransformToSplit = () => {
    if (!step.config) return;

    // Create variants from the list
    const variants: StepVariant[] = newVariants.map((v, idx) => ({
      id: `${step.id}_variant_${idx}`,
      label: v.label,
      percentage: v.percentage,
      regions: v.regions,
      config: {
        ...step.config!,
        variant: v.label,
        // DO NOT copy URL - backend will compute it
      },
    }));

    onTransformToSplit(variants, splitType);
    setOpen(false);
  };

  // ✅ FIX: All handlers now update local state to avoid stale closure issues
  // Changes are synced to parent only on "Save Changes" click
  const handleRemoveVariant = (variantId: string) => {
    if (localVariants.length <= 2) {
      // Don't allow removing if only 2 variants left (would become standard step)
      alert(t('manageVariants.minTwoVariants'));
      return;
    }

    setLocalVariants((prev) => prev.filter((v) => v.id !== variantId));
  };

  const handleUpdateVariantPercentage = (variantId: string, percentage: number) => {
    setLocalVariants((prev) => prev.map((v) => (v.id === variantId ? { ...v, percentage } : v)));
  };

  const handleUpdateVariantLabel = (variantId: string, label: string) => {
    setLocalVariants((prev) => prev.map((v) => (v.id === variantId ? { ...v, label } : v)));
  };

  const handleUpdateVariantRegions = (variantId: string, regions: string[]) => {
    setLocalVariants((prev) => prev.map((v) => (v.id === variantId ? { ...v, regions } : v)));
  };

  const handleAddVariant = () => {
    const newVariant: StepVariant = {
      id: `${step.id}_variant_${Date.now()}`,
      label: String.fromCharCode(65 + localVariants.length),
      percentage: 0,
      regions: [],
      config: step.variants![0].config,
    };
    setLocalVariants((prev) => [...prev, newVariant]);
  };

  const handleAutoNormalizeExisting = () => {
    setLocalVariants((prev) => {
      const total = prev.reduce((sum, v) => sum + v.percentage, 0);
      if (total === 0) {
        // Equal distribution if all are 0
        const equalPercentage = Math.floor(100 / prev.length);
        const remainder = 100 - equalPercentage * prev.length;
        return prev.map((v, i) => ({
          ...v,
          percentage: i === 0 ? equalPercentage + remainder : equalPercentage,
        }));
      } else {
        // Proportional normalization based on current values
        const normalized = prev.map((v) => ({
          ...v,
          percentage: Math.round((v.percentage / total) * 100),
        }));

        // Fix rounding errors by adjusting the first variant
        const newTotal = normalized.reduce((sum, v) => sum + v.percentage, 0);
        if (newTotal !== 100) {
          normalized[0].percentage += 100 - newTotal;
        }

        return normalized;
      }
    });
  };

  const totalPercentage = isStandardStep
    ? newVariants.reduce((sum, v) => sum + v.percentage, 0)
    : normalizedCurrentVariants.reduce((sum, v) => sum + v.percentage, 0);
  const availablePercentage = 100 - totalPercentage;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Only show trigger if not externally controlled */}
      {externalOpen === undefined && (
        <DialogTrigger asChild>
          {children || (
            <Button variant="outline" size="sm" className="h-10 gap-2 text-sm">
              <Shuffle className="h-4 w-4" />
              {isStandardStep ? t('manageVariants.createAbTest') : t('manageVariants.manageAbTest')}
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shuffle className="h-5 w-5" />
            {isStandardStep ? t('manageVariants.createAbTest') : t('manageVariants.manageAbTest')}
          </DialogTitle>
          <DialogDescription>
            {isStandardStep
              ? t('manageVariants.splitTrafficDescription')
              : t('manageVariants.managingVersions', { count: normalizedCurrentVariants.length })}
          </DialogDescription>
        </DialogHeader>

        {/* Standard Step → Split Step Transformation */}
        {isStandardStep ? (
          <div className="space-y-4">
            {/* Split Type Selector */}
            <div className="flex items-center gap-2">
              <Label className="text-sm">{t('manageVariants.splitType')}</Label>
              <Select value={splitType} onValueChange={(v: 'weighted' | 'geo') => setSplitType(v)}>
                <SelectTrigger className="h-9 w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weighted">{t('manageVariants.percentage')}</SelectItem>
                  <SelectItem value="geo">{t('manageVariants.geographic')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Variants List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('manageVariants.versions')}</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddNewVariantToCreation}
                  className="h-8 gap-1"
                >
                  <Plus className="h-3 w-3" />
                  {t('manageVariants.addVersion')}
                </Button>
              </div>

              <div className="space-y-2">
                {newVariants.map((variant, idx) => (
                  <div key={idx} className="space-y-2 rounded-md border p-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={variant.label}
                        onChange={(e) => handleUpdateNewVariant(idx, 'label', e.target.value)}
                        placeholder="A"
                        className="h-9 w-16"
                      />
                      {splitType === 'weighted' ? (
                        <>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={variant.percentage}
                            onChange={(e) =>
                              handleUpdateNewVariant(idx, 'percentage', parseInt(e.target.value) || 0)
                            }
                            placeholder="50"
                            className="h-9 w-20"
                          />
                          <span className="text-muted-foreground text-sm">%</span>
                        </>
                      ) : (
                        <CountryMultiSelect
                          countryCodes={countries.map((c) => c['alpha-2'])}
                          selected={variant.regions || []}
                          onChange={(regions) => handleUpdateNewVariant(idx, 'regions', regions)}
                          placeholder={t('manageVariants.selectCountries')}
                          className="h-9 flex-1"
                          contentClassName="z-[10001]"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveNewVariant(idx)}
                        disabled={newVariants.length <= 2}
                        className="ml-auto h-8 w-8 p-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Total Traffic - Only for weighted */}
            {splitType === 'weighted' && (
              <div className="flex items-center justify-between rounded-md border p-2">
                <span className="text-sm">{t('manageVariants.totalTraffic')}</span>
                <div className="flex items-center gap-2">
                  <Badge variant={totalPercentage === 100 ? 'default' : 'destructive'}>
                    {totalPercentage}%
                  </Badge>
                  {totalPercentage !== 100 && (
                    <Button variant="outline" size="sm" onClick={handleAutoNormalize} className="h-7">
                      {t('manageVariants.autoFix')}
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                {t('manageVariants.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={handleTransformToSplit}
                disabled={
                  splitType === 'weighted'
                    ? totalPercentage !== 100
                    : newVariants.some((v) => !v.regions || v.regions.length === 0)
                }
                className="gap-1"
              >
                <Shuffle className="h-3 w-3" />
                {t('manageVariants.createAbTestCount', { count: newVariants.length })}
              </Button>
            </div>
          </div>
        ) : (
          /* Manage Existing Variants */
          <div className="space-y-4">
            {/* Split Type Selector */}
            <div className="flex items-center gap-2">
              <Label className="text-sm">{t('manageVariants.splitType')}</Label>
              <Select value={splitType} onValueChange={(v: 'weighted' | 'geo') => setSplitType(v)}>
                <SelectTrigger className="h-9 w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weighted">{t('manageVariants.percentage')}</SelectItem>
                  <SelectItem value="geo">{t('manageVariants.geographic')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Variants List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('manageVariants.versions')}</Label>
                <Button variant="outline" size="sm" onClick={handleAddVariant} className="h-8 gap-1">
                  <Plus className="h-3 w-3" />
                  {t('manageVariants.addVersion')}
                </Button>
              </div>

              <div className="space-y-2">
                {normalizedCurrentVariants.map((variant) => (
                  <div key={variant.id} className="space-y-2 rounded-md border p-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={variant.label}
                        onChange={(e) => handleUpdateVariantLabel(variant.id, e.target.value)}
                        placeholder="A"
                        className="h-9 w-16"
                      />
                      {splitType === 'weighted' ? (
                        <>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={variant.percentage}
                            onChange={(e) =>
                              handleUpdateVariantPercentage(variant.id, parseInt(e.target.value) || 0)
                            }
                            placeholder="50"
                            className="h-9 w-20"
                          />
                          <span className="text-muted-foreground text-sm">%</span>
                        </>
                      ) : (
                        <CountryMultiSelect
                          key={`${variant.id}-${open ? 'open' : 'closed'}`}
                          countryCodes={countries.map((c) => c['alpha-2'])}
                          selected={variant.regions || []}
                          onChange={(regions) => handleUpdateVariantRegions(variant.id, regions)}
                          placeholder={t('manageVariants.selectCountries')}
                          className="h-9 flex-1"
                          contentClassName="z-[10001]"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveVariant(variant.id)}
                        disabled={localVariants.length <= 2}
                        className="ml-auto h-8 w-8 p-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Total Traffic - Only for weighted */}
            {splitType === 'weighted' && (
              <div className="flex items-center justify-between rounded-md border p-2">
                <span className="text-sm">{t('manageVariants.totalTraffic')}</span>
                <div className="flex items-center gap-2">
                  <Badge variant={totalPercentage === 100 ? 'default' : 'destructive'}>
                    {totalPercentage}%
                  </Badge>
                  {totalPercentage !== 100 && (
                    <Button variant="outline" size="sm" onClick={handleAutoNormalizeExisting} className="h-7">
                      {t('manageVariants.autoFix')}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Remove A/B Test */}
            {onRemoveSplit && !showRemoveConfirm && (
              <Button
                variant="outline"
                size="sm"
                className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground w-full"
                onClick={() => setShowRemoveConfirm(true)}
              >
                <Trash2 className="mr-2 h-3 w-3" />
                {t('manageVariants.convertToSinglePage')}
              </Button>
            )}

            {/* Remove Confirmation */}
            {showRemoveConfirm && onRemoveSplit && (
              <div className="border-destructive bg-destructive/10 space-y-2 rounded-md border-2 p-3">
                <Label className="text-sm font-medium">{t('manageVariants.keepVersion')}</Label>
                <Select value={selectedVariantToKeep} onValueChange={setSelectedVariantToKeep}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t('manageVariants.selectVersionToKeep')} />
                  </SelectTrigger>
                  <SelectContent>
                    {normalizedCurrentVariants.map((variant) => (
                      <SelectItem key={variant.id} value={variant.id}>
                        {variant.label} - {variant.config.variant || 'default'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowRemoveConfirm(false);
                      setSelectedVariantToKeep('');
                    }}
                    className="flex-1"
                  >
                    {t('manageVariants.cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      const variantToKeep = normalizedCurrentVariants.find(
                        (v) => v.id === selectedVariantToKeep,
                      );
                      if (variantToKeep) {
                        onRemoveSplit(variantToKeep);
                        setOpen(false);
                        setShowRemoveConfirm(false);
                        setSelectedVariantToKeep('');
                      }
                    }}
                    disabled={!selectedVariantToKeep}
                    className="flex-1 gap-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    {t('manageVariants.confirm')}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                {t('manageVariants.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  onUpdateVariants(normalizedCurrentVariants, splitType);
                  setOpen(false);
                }}
                disabled={
                  splitType === 'weighted'
                    ? totalPercentage !== 100
                    : normalizedCurrentVariants.some((v) => !v.regions || v.regions.length === 0)
                }
                className="gap-1"
              >
                <Shuffle className="h-3 w-3" />
                {t('manageVariants.saveChanges')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
